import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CUSTOMER_SEGMENT_LABELS, type Customer, type CustomerSegment, type Paginated } from '@cenox/shared';
import { ApiError, api, qs } from '../../lib/api';
import { useLocationFilter } from '../../lib/locationContext';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { count, date, money } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Pagination,
  TableSkeleton,
  Textarea,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { PhoneMatches } from './PhoneMatches';

export function CustomersPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [owingOnly, setOwingOnly] = useState(false);
  const [segment, setSegment] = useState<CustomerSegment | ''>('');
  const [creating, setCreating] = useState(false);

  const filters = qs({
    page,
    pageSize: 25,
    search: search || undefined,
    owingOnly: owingOnly || undefined,
    segment: segment || undefined,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['customers', business?.businessId, filters],
    queryFn: () => api.get<Paginated<Customer>>(path(`/customers${filters}`)),
    enabled: Boolean(business),
  });

  const timezone = business?.timezone ?? 'UTC';
  const currency = business?.currencyCode ?? 'USD';

  return (
    <>
      <PageHeader
        title="Customers"
        description="Who buys from you, and who still owes"
        action={
          can('customers:create') && (
            <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Add customer
            </Button>
          )
        }
      />

      <PhoneMatches />

      <PatronageSummary
        active={segment}
        onPick={(next) => {
          setSegment(next === segment ? '' : next);
          setPage(1);
        }}
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-56 flex-1">
            <Input
              placeholder="Search by name, phone or email…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              aria-label="Search customers"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={owingOnly}
              onChange={(event) => {
                setOwingOnly(event.target.checked);
                setPage(1);
              }}
              className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Owing only
          </label>
          <select
            aria-label="Segment"
            value={segment}
            onChange={(event) => {
              setSegment(event.target.value as CustomerSegment | '');
              setPage(1);
            }}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
          >
            <option value="">Every customer</option>
            {(Object.keys(CUSTOMER_SEGMENT_LABELS) as CustomerSegment[]).map((k) => (
              <option key={k} value={k}>
                {CUSTOMER_SEGMENT_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
      </Card>

      <Card padded={false}>
        {isPending ? (
          <TableSkeleton columns={4} />
        ) : error ? (
          <div className="p-4">
            <ErrorState message="Could not load customers." onRetry={() => refetch()} />
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<Users className="size-10" />}
            title={search || owingOnly || segment ? 'No customers match your filters' : 'No customers yet'}
            description={
              search || owingOnly || segment
                ? 'Try clearing a filter.'
                : 'Add customers to track who buys from you and who owes.'
            }
            action={
              can('customers:create') && !search && !owingOnly && !segment ? (
                <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                  Add customer
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Name</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Phone</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Segment</th>
                    <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium text-slate-600">Total spent</th>
                    <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium text-slate-600">Average</th>
                    <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium text-slate-600">Outstanding</th>
                    <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium text-slate-600">Purchases</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">First purchase</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Last purchase</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.data.map((customer) => (
                    <tr key={customer.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">
                        <Link to={`/customers/${customer.id}`} className="hover:text-brand-700 hover:underline">
                          {customer.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{customer.phone ?? '—'}</td>
                      <td className="px-4 py-3">
                        <SegmentBadge segment={customer.segment} />
                      </td>
                      <td className="px-4 py-3 text-right tabular">
                        {money(customer.totalSpent, currency)}
                      </td>
                      <td className="px-4 py-3 text-right tabular text-slate-600">
                        {customer.averagePurchase ? money(customer.averagePurchase, currency) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular">
                        {customer.totalOutstanding === '0.00' ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className="font-medium text-amber-700">
                            {money(customer.totalOutstanding, currency)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular text-slate-600">
                        {count(customer.transactionCount)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {date(customer.firstPurchaseAt, timezone)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {date(customer.lastPurchaseAt, timezone)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="divide-y divide-slate-100 md:hidden">
              {data.data.map((customer) => (
                <li key={customer.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/customers/${customer.id}`} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                        {customer.name}
                      </Link>
                      <div className="mt-1">
                        <SegmentBadge segment={customer.segment} />
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {customer.phone ?? 'No phone'} · {count(customer.transactionCount)} purchase
                        {customer.transactionCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-semibold tabular text-slate-900">
                        {money(customer.totalSpent, currency)}
                      </p>
                      {customer.totalOutstanding !== '0.00' && (
                        <p className="mt-0.5 text-xs font-medium tabular text-amber-700">
                          {money(customer.totalOutstanding, currency)} owed
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <Pagination
              page={data.meta.page}
              pageSize={data.meta.pageSize}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Modal open={creating} onClose={() => setCreating(false)} title="Add a customer">
        <CustomerForm
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['customers'] });
          }}
          onCancel={() => setCreating(false)}
        />
      </Modal>
    </>
  );
}

function CustomerForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const path = useBusinessPath();
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const workingLocation = useLocationFilter();

  const update =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post(path('/customers'), payload),
    onSuccess: onDone,
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not add the customer.');
      }
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    create.mutate({
      /*
        Which counter is adding them.

        A customer belongs to the business, but somebody confined to locations
        only sees the ones their own counter has served — so the working
        location travels with the write, exactly as it does for a sale. Empty
        for a member who sees everywhere, who needs no link to find them again.
      */
      ...workingLocation,
      name: form.name,
      phone: form.phone || null,
      email: form.email || null,
      address: form.address || undefined,
      notes: form.notes || undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {error && !Object.keys(fieldErrors).length && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Input
        label="Name"
        required
        autoFocus
        value={form.name}
        onChange={update('name')}
        error={fieldErrors.name}
      />
      <Input
        label="Phone"
        type="tel"
        value={form.phone}
        onChange={update('phone')}
        error={fieldErrors.phone}
        hint="Used to recognise repeat customers"
      />
      <Input
        label="Email"
        type="email"
        value={form.email}
        onChange={update('email')}
        error={fieldErrors.email}
      />
      <Textarea label="Address" value={form.address} onChange={update('address')} />
      <Textarea label="Notes" value={form.notes} onChange={update('notes')} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending}>
          Add customer
        </Button>
      </div>
    </form>
  );
}

const SEGMENT_TONE: Record<CustomerSegment, string> = {
  none: 'bg-slate-100 text-slate-600',
  first_time: 'bg-sky-50 text-sky-700',
  returning: 'bg-emerald-50 text-emerald-700',
  frequent: 'bg-brand-50 text-brand-700',
  inactive: 'bg-amber-50 text-amber-700',
};

export function SegmentBadge({ segment }: { segment: CustomerSegment }): ReactNode {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${SEGMENT_TONE[segment]}`}>
      {CUSTOMER_SEGMENT_LABELS[segment]}
    </span>
  );
}

interface CustomerAnalytics {
  thresholds: { recentDays: number; frequentPurchases: number };
  segments: Record<CustomerSegment, number>;
  newInWindow: number;
  returningInWindow: number;
}

/** Who is new, who comes back, who has stopped coming. Each card filters the list. */
function PatronageSummary({ active, onPick }: { active: CustomerSegment | ''; onPick: (s: CustomerSegment) => void }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const { data } = useQuery({
    queryKey: ['customers', business?.businessId, 'analytics'],
    queryFn: () => api.get<CustomerAnalytics>(path('/customers/analytics')),
    enabled: Boolean(business),
  });
  if (!data) return null;
  const days = data.thresholds.recentDays;
  const cards: Array<{ key: CustomerSegment; hint: string }> = [
    { key: 'first_time', hint: 'One purchase so far' },
    { key: 'returning', hint: 'Bought two or more times' },
    { key: 'frequent', hint: `${data.thresholds.frequentPurchases}+ purchases in ${days} days` },
    { key: 'inactive', hint: `Nothing in ${days} days` },
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
      {cards.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onPick(c.key)}
          aria-pressed={active === c.key}
          className={`rounded-xl border bg-white p-3 text-left transition hover:border-brand-300 ${active === c.key ? 'border-brand-500 ring-1 ring-brand-500' : 'border-slate-200'}`}
        >
          <p className="text-xs font-medium text-slate-500">{CUSTOMER_SEGMENT_LABELS[c.key]}</p>
          <p className="mt-1 text-xl font-semibold tabular text-slate-900">{count(data.segments[c.key])}</p>
          <p className="mt-0.5 text-xs text-slate-500">{c.hint}</p>
        </button>
      ))}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <p className="text-xs font-medium text-slate-500">Last {days} days</p>
        <p className="mt-1 text-xl font-semibold tabular text-slate-900">{count(data.newInWindow)} new</p>
        <p className="mt-0.5 text-xs text-slate-500">{count(data.returningInWindow)} came back</p>
      </div>
    </div>
  );
}
